import React from "react";
import { RouteComponentProps } from "react-router";

import withTitle from "../withTitle";
import { V2 } from "../../Api";
import { formatName, formatDateTimeLocal, formatDateLocalMonth } from "../../Utilities";

import "../../question.css";

interface IMetricsAssessmentDetailsViewState {
    isLoading: boolean | string;

    assessment: any;
}

class MetricsAssessmentDetailsView extends React.Component<RouteComponentProps<any>, IMetricsAssessmentDetailsViewState> {
    constructor(props: Readonly<RouteComponentProps<any>>) {
        super(props);

        const state: IMetricsAssessmentDetailsViewState = {
            isLoading: true,

            assessment: {},
        };

        this.state = state;

        this.loadAssessment = this.loadAssessment.bind(this);
    }

    public componentDidMount() {
        this.loadAssessment();
    }

    public render() {
        if (this.state.isLoading === true) {
            return <div className="viewer-root">
                <div className="spinner spinner-center lds-dual-ring-big" />
            </div>;
        }

        if (typeof this.state.isLoading === "string") {
            return <div className="viewer-root">
                <div className="error-text">{this.state.isLoading}</div>
            </div>;
        }

        return (
            <div className="viewer-root viewer-narrow info-container question-container">
                <header className="info-header">
                    <h2>{formatName(this.state.assessment.employee.name)} &mdash; {formatDateLocalMonth(this.state.assessment.date)}</h2>
                    <small className="small-text">Last updated {formatDateTimeLocal(this.state.assessment.lastUpdated)}</small>
                    <small className="small-text">Assessment by {formatName(this.state.assessment.assessedBy?.name)}</small>
                </header>
                {this.state.assessment.answers.length < 1
                    ? <section className="info-block error">
                        <span>No questions were answered</span>
                    </section>
                    : this.state.assessment.answers.map((a: any) => <section key={a.id} className={"info-block" + ((a.answerText || a.answerNumber || a.answerNumber === 0) ? " answered" : " warning")}>
                        <span className="question-text">{a.questionText}</span>
                        <span className="question-answer">{this.renderQuestionType(a)}</span>
                    </section>)
                }
            </div>
        );
    }

    private async loadAssessment() {
        try {
            const assessment = await V2.get<any>(`metrics/assessments/${this.props.match.params.assessment_id}`);

            this.setState({
                isLoading: false,
                assessment
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load assessment"
            });
        }
    }

    private renderQuestionType(a: any) {
        const { questionType } = a;
        const answer = (a.answerText || a.answerNumber?.toString()) || "";

        if (!answer)
            return <span className="text-light"><small>Not answered</small></span>;

        switch (questionType) {
            case "text":
            case "text.multiline":
            case "scale.1-5.generic":
            case "scale.m2-2.generic":
            default:
                return <span>{answer}</span>;
            case "yesno":
            case "yesno.na":
            case "scale.m2-2.change":
            case "scale.1-5.expectation":
            case "scale.1-5.expectation2":
            case "scale.1-5.expectation2.na":
            case "scale.1-5.agree":
                return this.getRadioText(questionType, a.answerNumber);
        }
    }

    private getRadioText(type: string, value: number) {
        if (value === undefined || value === null)
            return <span className="text-light"><small>Not answered</small></span>;

        switch (type) {
            case "yesno":
            case "yesno.na":
                switch (value) {
                    case -1: return "N/A";
                    case 0: return "No";
                    case 1: return "Yes";
                    default: return value.toString();
                }
            case "scale.m2-2.change":
                switch (value) {
                    case -2: return "Greatly Worsened";
                    case -1: return "Worsened";
                    case 0: return "No Change";
                    case 1: return "Improved";
                    case 2: return "Greatly Improved";
                    default: return value.toString();
                }
            case "scale.1-5.expectation":
            case "scale.1-5.expectation.na":
                switch (value) {
                    case -1: return "N/A";
                    case 1: return "Unacceptable";
                    case 2: return "Needs Improvement";
                    case 3: return "Meets Expectations";
                    case 4: return "Exceeds Expectations";
                    case 5: return "Outstanding";
                    default: return value.toString();
                }
            case "scale.1-5.expectation2":
            case "scale.1-5.expectation2.na":
                switch (value) {
                    case -1: return "N/A";
                    case 1: return "We Need to talk";
                    case 2: return "Needs Improvement";
                    case 3: return "Meets Expectations";
                    case 4: return "Exceeds Expectations";
                    case 5: return "Outstanding";
                    default: return value.toString();
                }
            case "scale.1-5.agree":
                switch (value) {
                    case 1: return "Strongly Disagree";
                    case 2: return "Disagree";
                    case 3: return "Neutral";
                    case 4: return "Agree";
                    case 5: return "Strongly Agree";
                    default: return value.toString();
                }
            default:
                return <span className="error-box slim inline">Unknown question type <b>{type}</b></span>;
        }
    }

    private renderQuestionRadio(id: string, answer: string, value: string, label?: string) {
        return <li className="question-checkbox">
            <input type="radio" name={`q_${id}`} id={`q_${id}_${value}`} value={value} checked={answer === value} />
            <label htmlFor={`q_${id}_${value}`} tabIndex={0}>
                {label || value}
                <span />
            </label>
        </li>;
    }
}

export default withTitle("Employee Assessment", MetricsAssessmentDetailsView);
